// Dropdown — subtle fade + slide-down on open/close.
//
// Reads `data-open` ("true"|"false") on the menu div; manages display via
// inline style so LiveView attribute diffs (only `data-open` changes
// server-side) don't fight the animation.
//
// Race the helpers must survive: open → close → open in rapid succession.
// The original `fromTo` in `animateIn` always reset opacity to 0 first,
// flashing the user when re-opening mid-close. The fix tracks visibility
// via the inline `display` style — if mid-tween (display === "block"), we
// `to` from current values; if fully closed (display !== "block"), we
// `fromTo` from the closed-state values.

import gsap from "gsap";
import {
  POPOVER_HIDDEN_VARS,
  POPOVER_VISIBLE_VARS,
  ensurePerspective,
} from "./animation-perspective";
import type { LiveViewHook } from "./types";

export interface DropdownState {
  _lastOpen: boolean;
}

export const dropdownAnimateIn = (el: HTMLElement): void => {
  gsap.killTweensOf(el);
  ensurePerspective(el);
  const wasHidden = el.style.display !== "block";
  el.style.display = "block";
  if (wasHidden) {
    gsap.fromTo(
      el,
      { ...POPOVER_HIDDEN_VARS },
      {
        ...POPOVER_VISIBLE_VARS,
        duration: 0.22,
        ease: "power3.out",
      },
    );
  } else {
    // Re-opening mid-out: continue from current visual state instead of
    // snapping back to opacity 0 first (would flash the user).
    gsap.to(el, {
      ...POPOVER_VISIBLE_VARS,
      duration: 0.22,
      ease: "power3.out",
    });
  }
};

export const dropdownAnimateOut = (el: HTMLElement): void => {
  gsap.killTweensOf(el);
  ensurePerspective(el);
  // Override the CSS `[data-open="false"] { display: none }` rule so the
  // element stays visible long enough to animate.
  el.style.display = "block";
  gsap.to(el, {
    ...POPOVER_HIDDEN_VARS,
    duration: 0.16,
    ease: "power2.in",
    onComplete: () => {
      // Re-check current state — a fast open-close-open could have us closing
      // an element the user just re-opened.
      if (el.dataset["open"] !== "true") {
        el.style.display = "none";
      }
    },
  });
};

export const Dropdown = {
  mounted(this: LiveViewHook<never> & DropdownState) {
    const open = this.el.dataset["open"] === "true";
    this._lastOpen = open;
    ensurePerspective(this.el);
    // CSS already hides closed dropdowns via the `[data-open="false"]` rule,
    // so the closed initial state has zero FOUC. We only need to neutralize
    // the transform so a later open animation starts from the right state.
    if (!open) {
      gsap.set(this.el, { ...POPOVER_HIDDEN_VARS });
    }
  },
  updated(this: LiveViewHook<never> & DropdownState) {
    const open = this.el.dataset["open"] === "true";
    if (open === this._lastOpen) return;
    this._lastOpen = open;
    if (open) {
      dropdownAnimateIn(this.el);
    } else {
      dropdownAnimateOut(this.el);
    }
  },
};
